import * as React from "react";
import apiClient from "@/api-client";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import { PlayCircleIcon, Link, XIcon } from "@phosphor-icons/react";

import { CallLog } from "@/hooks/api/use-call-logs";
import { toast } from "sonner";
import { BASE_URL } from "@/runtime-env";

interface RecordingModalProps {
	open: boolean;
	onClose: () => void;
	callLog: CallLog | null;
}

export function RecordingModal({ open, onClose, callLog }: RecordingModalProps): React.JSX.Element {
	const [isLoading, setIsLoading] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);
	const [audioSrc, setAudioSrc] = React.useState<string | null>(null);

	const audioRef = React.useRef<HTMLAudioElement | null>(null);
	const abortControllerRef = React.useRef<AbortController | null>(null);

	// Load recording when modal opens or callLogId changes
	React.useEffect(() => {
		if (open && callLog?.id && !audioSrc && !isLoading && !abortControllerRef.current) {
			loadRecording();
		}
	}, [open, callLog?.id]);

	// Cleanup on unmount or close
	React.useEffect(() => {
		if (!open) {
			if (audioRef.current) {
				if (audioRef.current.src?.startsWith("blob:")) {
					URL.revokeObjectURL(audioRef.current.src);
				}
				audioRef.current.src = "";
			}
			setAudioSrc(null);
		}

		return () => {
			const controller = abortControllerRef.current;
			if (controller) {
				controller.abort();
				abortControllerRef.current = null;
			}

			const audioEl = audioRef.current;
			if (audioEl && audioEl.src?.startsWith("blob:")) {
				URL.revokeObjectURL(audioEl.src);
			}
		};
	}, [open]);

	const loadRecording = React.useCallback(async () => {
		if (!callLog?.id) return;

		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		const abortController = new AbortController();
		abortControllerRef.current = abortController;

		setIsLoading(true);
		setError(null);

		try {
			const response = await apiClient.get(`/calls/${callLog?.id}/recording`, {
				responseType: "blob",
				signal: abortController.signal,
			});

			if (abortController.signal.aborted) {
				return;
			}

			if (response.data && audioRef.current) {
				const blob = new Blob([response.data], { type: "audio/mpeg" });
				const blobUrl = URL.createObjectURL(blob);

				audioRef.current.src = blobUrl;
				audioRef.current.preload = "metadata";
				// Ensure metadata (duration) is available for native controls
				audioRef.current.load();
				setAudioSrc(blobUrl);

				// Try to auto-play after loading; browsers may block without user gesture
				try {
					await audioRef.current.play();
				} catch {
					// Ignore autoplay block; user can press play
				}
			}
		} catch (error_: any) {
			if (error_.name === "AbortError" || error_.message === "canceled") {
				return;
			}
			console.error("Failed to load recording:", error_);
			setError("Failed to load recording. Please try again.");
		} finally {
			setIsLoading(false);
			if (abortControllerRef.current === abortController) {
				abortControllerRef.current = null;
			}
		}
	}, [callLog]);

  const handleShareLink = () => {
    const url = `${BASE_URL}/call-logs?play=${callLog?.id}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			sx={{
				"& .MuiDialog-paper": {
					borderRadius: 2,
				},
			}}
		>
			<DialogTitle
				sx={{
					display: "flex",
					alignItems: "flex-start",
					justifyContent: "space-between",
					pb: 2,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<PlayCircleIcon size={24} color="var(--mui-palette-primary-main)" style={{ flexShrink: 0 }} />
					<Stack>
						<Typography variant="h6" fontSize={18}>
							Call Recording
						</Typography>
						{callLog?.id && (
							<Typography variant="caption" color="text.secondary">
								{callLog?.id}
							</Typography>
						)}
					</Stack>
				</Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Copy audio link" arrow>
            <IconButton size="small" onClick={handleShareLink}>
              <Link size={20} color="var(--mui-palette-info-dark)" />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small">
            <XIcon size={20} />
          </IconButton>
        </Box>
			</DialogTitle>

			<DialogContent sx={{ pt: 2, minHeight: "96px" }}>
				<Stack spacing={3}>
					<Box sx={{ position: "relative", mt: 2 }}>
						<Box sx={{ opacity: isLoading || error ? 0 : 1 }}>
							<audio ref={audioRef} controls preload="metadata" style={{ width: "100%", outline: "none" }} />
						</Box>
						{isLoading && (
							<Box
								sx={{
									position: "absolute",
									top: 0,
									left: 0,
									right: 0,
									bottom: 0,
									// display: "flex",
									// flexDirection: "column",
									// justifyContent: "center",
									// alignItems: "center",
								}}
							>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
									Loading recording...
								</Typography>
								<LinearProgress />
							</Box>
						)}

						{error && (
							<Box
								sx={{
									position: "absolute",
									top: 0,
									left: 0,
									right: 0,
									bottom: 0,
								}}
							>
								<Box
									sx={{
										p: 2,
										bgcolor: "error.light",
										color: "error.contrastText",
										borderRadius: 1,
									}}
								>
									<Typography variant="body2">{error}</Typography>
								</Box>
							</Box>
						)}
					</Box>
				</Stack>
			</DialogContent>
		</Dialog>
	);
}
